import { useEffect, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import AuthService from "@/services/AuthService";

const MESSAGING_API_URL = import.meta.env.VITE_MESSAGING_API_URL as string;

export interface VisitorSession {
  id: number;
  sessionId: string;
  entryPage: string | null;
  currentPage: string | null;
  referrer: string | null;
  pageCount: number;
  isChatting: boolean;
  conversationId: number | null;
  firstSeen: string;
  lastSeen: string;
  browser: string | null;
  os: string | null;
  country: string | null;
  language: string | null;
  secondsOnSite: number;
}

interface Options {
  enabled: boolean;
  onUpdate: (session: VisitorSession) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

/**
 * Connects to the messaging ClientHub and listens for `visitor-updated` events.
 * Only admins receive these — non-admins are not in the AdminGroup.
 * Returns a cleanup function; the caller manages connection lifecycle via the ref.
 */
export function useVisitorSignalR({ enabled, onUpdate, onConnected, onDisconnected }: Options) {
  const hubRef = useRef<signalR.HubConnection | null>(null);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const connect = useCallback(async () => {
    if (!enabled || !MESSAGING_API_URL) return;
    if (hubRef.current?.state === signalR.HubConnectionState.Connected) return;

    const hub = new signalR.HubConnectionBuilder()
      .withUrl(`${MESSAGING_API_URL}/ClientHub`, {
        accessTokenFactory: () => AuthService.token.token,
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect([0, 2000, 5000, 15000, 30000])
      .build();

    hub.on("visitor-updated", (session: VisitorSession) => {
      onUpdateRef.current(session);
    });

    hub.onreconnected(() => onConnected?.());
    hub.onclose(() => onDisconnected?.());

    hubRef.current = hub;

    try {
      await hub.start();
      onConnected?.();
    } catch {
      // Connection failure is non-fatal — callers keep their fallback poll
    }
  }, [enabled, onConnected, onDisconnected]);

  useEffect(() => {
    connect();
    return () => {
      hubRef.current?.stop();
      hubRef.current = null;
    };
  }, [connect]);

  return hubRef;
}
